/**
 * «Покрытие техкартами» — шапка экрана назначения (макет v2-04).
 *
 * Смысл блока: без покрытия экономика варианта неполная, и это должно быть
 * видно ДО того, как агроном обрадуется марже. Непокрытая площадь не
 * превращается в ноль и не размазывается по остальным культурам — она
 * названа отдельно, в гектарах, с указанием, где её закрывать.
 */
import { Card, CardContent } from '@/components/ui/card';
import { TileGrid } from '@/components/ui/tile-grid';
import { coverageHeadline, formatHa, formatPct, type Coverage, type PlanRow } from '@/lib/planning/planVariant';

export function CoverageCard({ coverage, rows }: { coverage: Coverage; rows: PlanRow[] }) {
  const unassigned = rows.filter((r) => r.state === 'unassigned');
  const okPct = coverage.total_ha > 0 ? (coverage.assigned_ha / coverage.total_ha) * 100 : 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="text-sm font-semibold text-foreground">Покрытие техкартами</h2>
          <span className="ml-auto text-xs text-muted-foreground">
            без покрытия экономика варианта неполная
          </span>
        </div>

        <div className="mt-3 text-2xl font-semibold tabular-nums text-foreground">
          {coverageHeadline(coverage)}
        </div>
        <div
          className="mt-2 flex h-3 w-full overflow-hidden rounded-full border border-border bg-muted"
          role="img"
          aria-label={`Назначено ${formatHa(coverage.assigned_ha)} из ${formatHa(coverage.total_ha)}`}
        >
          <span className="block h-full bg-primary" style={{ width: `${okPct}%` }} />
          <span className="block h-full bg-accent-brand" style={{ width: `${100 - okPct}%` }} />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {formatPct(coverage.pct)} площади под культурами. Пар ({formatHa(coverage.fallow_ha)}) в покрытие
          не входит: техкарта заводится на культуру.
        </p>

        <TileGrid cols={3} className="mt-4">
          <Tile
            value={`${coverage.rows_assigned} из ${coverage.rows_total}`}
            label="строк плана с назначенным вариантом техкарты"
          />
          <Tile
            value={formatHa(coverage.unassigned_ha)}
            warn={coverage.unassigned_ha > 0}
            label={
              unassigned.length > 0
                ? `${unassigned.map((r) => `${r.crop_name} ${r.predecessor_name ? `по ${r.predecessor_name.toLowerCase()}` : ''}`.trim()).join(', ')} — вариант не выбран, затраты и маржа по ним в сводку не попадают`
                : 'вся площадь под культурами покрыта'
            }
          />
          <Tile
            value={formatHa(coverage.no_crop_ha)}
            warn={coverage.no_crop_ha > 0}
            label="культура вообще не назначена — это правится в распределении культур по полям"
          />
        </TileGrid>
      </CardContent>
    </Card>
  );
}

function Tile({ value, label, warn = false }: { value: string; label: string; warn?: boolean }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className={warn ? 'text-lg font-semibold tabular-nums text-accent-brand' : 'text-lg font-semibold tabular-nums text-foreground'}>
        {value}
      </div>
      <div className="mt-1 text-xs leading-5 text-muted-foreground">{label}</div>
    </div>
  );
}
